import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FieldDef, PublicForm } from '@jfh/intake-shared';
import {
  applyCountryDefaults,
  applySubFieldDefaults,
  filledAddressKeys,
  withCopiedAddresses,
  augmentCountryAttributes,
  companyAddressValue,
  companyNameCase,
  companyLockTargets,
  DEFAULT_COUNTRY_ISO,
  enabledCountries,
  visibleFields,
} from '@jfh/intake-shared';
import type { CompanyLookupOutcome } from './lib/api.js';
import { checkableVat, vatFieldError, vatStateFromLookup, type VatLookupOutcome, type VatState } from './lib/vat.js';
import { completeCompanyNumber, companyFieldError, stateFromLookup, type CompanyState } from './lib/company.js';
import { buildPages, setCountryNames, validatePage, type FormValues, type Page } from './lib/steps.js';

/** Starting answers: each field's default value (a lone tick box stores true/false). */
export function seedValues(fields: FieldDef[]): FormValues {
  const seed: FormValues = {};
  for (const f of fields) {
    if (f.defaultValue === undefined || f.type === 'heading') continue;
    const isBoolCheckbox = f.type === 'checkbox' && !(f.options && f.options.length);
    seed[f.id] = isBoolCheckbox ? f.defaultValue === 'true' : f.defaultValue;
  }
  return seed;
}

export interface ApplicantFormOptions {
  /** Companies House lookup for company-number fields (F8). */
  lookupCompany: (number: string) => Promise<CompanyLookupOutcome>;
  /** HMRC VAT check for `vat` fields (F8 P2); without it, VAT numbers aren't checked live. */
  lookupVat?: (number: string) => Promise<VatLookupOutcome>;
  /** Starting answers; defaults to `seedValues(form.fields)`. */
  initialValues?: () => FormValues;
}

/**
 * The applicant's side of a form: pages, answers, show/hide rules, the
 * Companies House lookup and page validation. Shared by the public renderer
 * and the admin builder's "Try it out" (F9 P3), so both behave the same.
 */
export function useApplicantForm(form: PublicForm, opts: ApplicantFormOptions) {
  const pages = useMemo(() => buildPages(form.fields), [form.fields]);
  // Address country options come from iQ (/countries: Code + Name) when available
  // — display Name, store/submit Code; fall back to the built-in list otherwise.
  const countries = useMemo(
    () =>
      form.countryOptions?.length
        ? form.countryOptions.map((o) => ({ iso2: o.value, name: o.label, dial: '' }))
        : enabledCountries(form.disabledCountries),
    [form.countryOptions, form.disabledCountries],
  );
  // Map country Code → Name so the review step shows names (value stays the Code) —
  // works for both the iQ list and the built-in fallback.
  setCountryNames(countries.map((c) => ({ value: c.iso2, label: c.name })));
  const defaultCountryIso = form.defaultCountry || DEFAULT_COUNTRY_ISO;
  // Selected-country lookup attributes (e.g. D_EUCountry), keyed by country code,
  // so rules can condition on a country attribute from iQ's /countries service.
  const countryData = useMemo(() => {
    const m: Record<string, Record<string, string>> = {};
    for (const o of form.countryOptions ?? []) if (o.data) m[o.value] = o.data;
    return m;
  }, [form.countryOptions]);
  const total = pages.length + 1; // + Review & submit
  const reviewIndex = total - 1;

  const [step, setStep] = useState(0);
  // The furthest step reached, so the progress bar can go back to any of them.
  const [furthest, setFurthest] = useState(0);
  useEffect(() => setFurthest((f) => Math.max(f, step)), [step]);
  const [values, setValues] = useState<FormValues>(() =>
    opts.initialValues ? opts.initialValues() : seedValues(form.fields),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Values augmented with `<addressField>.country.<attr>` keys, for evaluating
  // visibility rules that depend on the selected country's lookup attributes.
  const visValues = useMemo(
    () => augmentCountryAttributes(values, form.fields, countryData),
    [values, form.fields, countryData],
  );
  // Cascading visibility for the whole form: a field whose driver is hidden is
  // hidden too (e.g. TSS Code once Region drops out for a non-UK country).
  const vis = useMemo(() => visibleFields(form.fields, visValues), [form.fields, visValues]);

  const isReview = step === reviewIndex;

  // A section is "active" only when at least one of its fields is currently
  // visible; sections whose fields are all hidden by rules are skipped.
  const pageActive = useCallback(
    (p: Page) => p.fields.some((f) => f.type !== 'hidden' && vis[f.id]),
    [vis],
  );
  const nextActiveStep = (from: number) => {
    let j = from + 1;
    while (j < pages.length && !pageActive(pages[j]!)) j++;
    return Math.min(j, reviewIndex);
  };
  const prevActiveStep = (from: number) => {
    let j = from - 1;
    while (j >= 0 && !pageActive(pages[j]!)) j--;
    return j < 0 ? from : j;
  };

  // If the current section has no visible fields (on load, or after a value
  // change emptied it), skip forward to the next active section / the review.
  useEffect(() => {
    if (isReview) return;
    const p = pages[step];
    if (p && !pageActive(p)) {
      let j = step + 1;
      while (j < pages.length && !pageActive(pages[j]!)) j++;
      setStep(Math.min(j, reviewIndex));
    }
  }, [step, isReview, pages, pageActive, reviewIndex]);

  // Companies House lookup (F8): a complete company number is looked up; an
  // active company fills + locks the name and registered address it targets.
  const companyFields = useMemo(
    () => form.fields.filter((f) => f.type === 'company_number'),
    [form.fields],
  );
  const lockTargets = useMemo(() => companyLockTargets(form.fields), [form.fields]);
  const [companies, setCompanies] = useState<Record<string, CompanyState>>({});
  const companiesRef = useRef(companies);
  companiesRef.current = companies;
  const valuesRef = useRef(values);
  valuesRef.current = values;
  const lookupRef = useRef(opts.lookupCompany);
  lookupRef.current = opts.lookupCompany;
  const lookupTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  /** Fill (company) or clear (null) the fields a company field locks. */
  const fillTargets = useCallback(
    (companyFieldId: string, company: CompanyState | null) => {
      const found = company?.kind === 'found' ? company.company : null;
      setValues((prev) => {
        const next = { ...prev };
        for (const [targetId, sourceId] of lockTargets) {
          if (sourceId !== companyFieldId) continue;
          const target = form.fields.find((f) => f.id === targetId);
          if (!target) continue;
          if (target.type === 'address') {
            next[targetId] = found ? companyAddressValue(found.address, target) : undefined;
          } else {
            next[targetId] = found ? companyNameCase(found.name) : '';
          }
        }
        return next;
      });
    },
    [lockTargets, form.fields],
  );

  const companyNumbers = companyFields.map((f) => String(values[f.id] ?? '')).join('|');
  useEffect(() => {
    for (const f of companyFields) {
      const number = completeCompanyNumber(valuesRef.current[f.id]);
      const cur = companiesRef.current[f.id];
      if (cur && cur.number === number) continue;
      clearTimeout(lookupTimers.current[f.id]);
      if (cur?.kind === 'found') fillTargets(f.id, null); // number changed: unlock + clear
      if (!number) {
        if (cur) {
          setCompanies((prev) => {
            const next = { ...prev };
            delete next[f.id];
            return next;
          });
        }
        continue;
      }
      setCompanies((prev) => ({ ...prev, [f.id]: { kind: 'checking', number } }));
      lookupTimers.current[f.id] = setTimeout(() => {
        void lookupRef.current(number).then((r) => {
          // Ignore an answer for a number the applicant has since changed.
          if (completeCompanyNumber(valuesRef.current[f.id]) !== number) return;
          const state = stateFromLookup(number, r);
          setCompanies((prev) => ({ ...prev, [f.id]: state }));
          if (state.kind === 'found') fillTargets(f.id, state);
        });
      }, 400);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyNumbers]);

  // HMRC VAT check (F8 P2): once a UK number is complete, check it (debounced).
  const vatFields = useMemo(() => form.fields.filter((f) => f.type === 'vat'), [form.fields]);
  const [vats, setVats] = useState<Record<string, VatState>>({});
  const vatsRef = useRef(vats);
  vatsRef.current = vats;
  const vatLookupRef = useRef(opts.lookupVat);
  vatLookupRef.current = opts.lookupVat;
  const vatTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const vatNumbers = vatFields.map((f) => String(values[f.id] ?? '')).join('|');
  useEffect(() => {
    if (!vatLookupRef.current) return;
    for (const f of vatFields) {
      const target = checkableVat(valuesRef.current[f.id]);
      const cur = vatsRef.current[f.id];
      if (cur && target && cur.number === target.display) continue;
      clearTimeout(vatTimers.current[f.id]);
      if (!target) {
        if (cur) {
          setVats((prev) => {
            const next = { ...prev };
            delete next[f.id];
            return next;
          });
        }
        continue;
      }
      setVats((prev) => ({ ...prev, [f.id]: { kind: 'checking', number: target.display } }));
      vatTimers.current[f.id] = setTimeout(() => {
        void vatLookupRef.current!(target.vrn).then((r) => {
          if (checkableVat(valuesRef.current[f.id])?.display !== target.display) return;
          setVats((prev) => ({ ...prev, [f.id]: vatStateFromLookup(target.display, r) }));
        });
      }, 400);
    }
  }, [vatNumbers, vatFields]);

  /** Fields currently locked by a found company → their lock note. */
  const locked = useMemo(() => {
    const m = new Set<string>();
    for (const [targetId, sourceId] of lockTargets) {
      if (companies[sourceId]?.kind === 'found' && vis[sourceId]) m.add(targetId);
    }
    return m;
  }, [lockTargets, companies, vis]);

  // Addresses linked to a country question take its answer as their country,
  // and contact lines linked to a question (e.g. Company name) its answer:
  // when empty, or still the question's previous answer. See
  // applyCountryDefaults / applySubFieldDefaults.
  const countryCodes = useMemo(() => new Set(countries.map((c) => c.iso2)), [countries]);
  const prevValues = useRef<FormValues>({});
  useEffect(() => {
    const next = applySubFieldDefaults(
      form.fields,
      prevValues.current,
      applyCountryDefaults(form.fields, prevValues.current, values, countryCodes, locked),
    );
    prevValues.current = next;
    if (next !== values) setValues(next as FormValues);
  }, [values, form.fields, countryCodes, locked]);

  /** Page validation plus the company checks; locked fields aren't re-checked. */
  const pageErrors = (p: Page): Record<string, string> => {
    // A copy of a Companies House address ("Same as registered address"),
    // as an address question or on a contact, isn't re-checked either.
    const errs = validatePage(
      p,
      visValues,
      vis,
      withCopiedAddresses(form.fields, values, locked),
      filledAddressKeys(form.fields, values, locked),
    );
    for (const f of p.fields) {
      if (f.type !== 'company_number' || !vis[f.id] || errs[f.id]) continue;
      const e = companyFieldError(values[f.id], companies[f.id]);
      if (e) errs[f.id] = e;
    }
    if (opts.lookupVat) {
      for (const f of p.fields) {
        if (f.type !== 'vat' || !vis[f.id] || errs[f.id]) continue;
        const e = vatFieldError(values[f.id], vats[f.id]);
        if (e) errs[f.id] = e;
      }
    }
    return errs;
  };

  const setValue = useCallback((id: string, v: unknown) => {
    setValues((prev) => ({ ...prev, [id]: v }));
    setErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  /** Validate the current page; move on if it passes. Returns whether it moved. */
  const goNext = (): boolean => {
    const page = pages[step];
    if (page) {
      const errs = pageErrors(page);
      if (Object.keys(errs).length > 0) {
        setErrors(errs);
        return false;
      }
    }
    setErrors({});
    setStep(nextActiveStep(step));
    return true;
  };

  const goBack = () => {
    setErrors({});
    setStep(prevActiveStep(step));
  };

  /**
   * Final guard before sending: every page's errors. On failure, shows them and
   * jumps to the first page with one; returns whether everything passed.
   */
  const checkAll = (): boolean => {
    const allErrors: Record<string, string> = {};
    for (const p of pages) Object.assign(allErrors, pageErrors(p));
    if (Object.keys(allErrors).length === 0) return true;
    setErrors(allErrors);
    const idx = pages.findIndex((p) => p.fields.some((f) => allErrors[f.id]));
    if (idx >= 0) setStep(idx);
    return false;
  };

  /** Start again with fresh answers. */
  const reset = (fresh: FormValues = seedValues(form.fields)) => {
    setValues(fresh);
    setErrors({});
    setCompanies({});
    setVats({});
    setStep(0);
    setFurthest(0);
  };

  // Progress reflects only the active (non-skipped) sections, plus the review.
  const activeCount = pages.filter(pageActive).length;
  // The step each progress item opens: the active pages, then the review.
  const targets = [...pages.flatMap((p, i) => (pageActive(p) ? [i] : [])), reviewIndex];
  const progress = {
    step: isReview ? activeCount : Math.max(0, pages.slice(0, step + 1).filter(pageActive).length - 1),
    total: activeCount + 1,
    /** Each active step's name, then "Check and send" (F10 P6). */
    labels: [...pages.filter(pageActive).map((p) => p.title), 'Check and send'],
    /** Whether progress item `i` can be clicked: a step already reached, not the one showing. */
    canPick: (i: number) => targets[i] !== undefined && targets[i]! <= furthest && targets[i] !== step,
    /** Open progress item `i`. */
    pick: (i: number) => {
      const t = targets[i];
      if (t === undefined || t > furthest) return;
      setErrors({});
      setStep(t);
    },
  };
  /** The current errors in question order, for the summary at the top. */
  const errorList = form.fields
    .filter((f) => errors[f.id])
    .map((f) => ({ id: f.id, message: errors[f.id]! }));
  const hasPrevSection = pages.slice(0, step).some(pageActive);

  return {
    form,
    pages,
    reviewIndex,
    isReview,
    step,
    setStep,
    values,
    setValues,
    setValue,
    errors,
    setErrors,
    visValues,
    vis,
    pageActive,
    companies,
    vats,
    /** VAT numbers are checked with HMRC as they're typed. */
    checksVat: Boolean(opts.lookupVat),
    locked,
    countries,
    defaultCountryIso,
    goNext,
    goBack,
    checkAll,
    reset,
    progress,
    errorList,
    activeCount,
    hasPrevSection,
  };
}

export type ApplicantForm = ReturnType<typeof useApplicantForm>;
